/**
 * Form Data API Client
 * Form submission and record retrieval — mirrors old atlas-form-sdk pattern.
 */

import { BaseHttpClient } from './http.client';
import { atlasApiConfig } from './config';
import type { ApiResponse, FormRecord } from '@atlas-forms/types-js';

export interface ListFormDataRequest {
  PageInfo?: {
    PageNumber?: number;
    PageSize?: number;
    SortBy?: string;
    SortOrder?: 'asc' | 'desc';
  };
  SearchData?: string;
  CreatedFrom?: string;
  CreatedTo?: string;
  FormID?: number;
}

export interface ListFormDataResponse {
  Data: FormRecord[];
  PageInfo: {
    PageNumber: number;
    PageSize: number;
    TotalPages: number;
    TotalRecords: number;
  };
}

const EP = {
  SUBMIT:   '/api/v1/atlas/form-data/submit',
  LIST:     '/api/v1/atlas/form-data/list',
  BY_FORM:  '/api/v1/atlas/form-data/by-form',
  GET:      '/api/v1/atlas/form-data/get',
  DELETE:   (id: number) => `/api/v1/atlas/form-data/${id}`,
} as const;

export class FormDataApiClient extends BaseHttpClient {
  constructor(config?: { baseUrl?: string; timeout?: number; token?: string; tenantId?: number }) {
    super({
      baseUrl: config?.baseUrl ?? atlasApiConfig.baseUrl,
      timeout: config?.timeout ?? atlasApiConfig.timeout,
      token: config?.token ?? atlasApiConfig.token,
      tenantId: config?.tenantId ?? atlasApiConfig.tenantId,
    });
  }

  async submitFormData(request: {
    FormID: number;
    FormData: string;          // JSON.stringify of field values
    FormVersion?: string;
  }): Promise<ApiResponse<FormRecord>> {
    return this.postQuery<FormRecord>(EP.SUBMIT, request);
  }

  async listAll(request: ListFormDataRequest = {}): Promise<ApiResponse<ListFormDataResponse>> {
    // Build request with PageInfo and filters
    const pageInfo = request.PageInfo ?? { PageNumber: 1, PageSize: 20 };
    const body: Record<string, unknown> = {
      PageInfo: pageInfo,
    };

    // Add optional filter fields
    if (request.SearchData !== undefined) {
      body.SearchData = request.SearchData;
    }
    if (request.CreatedFrom !== undefined) {
      body.CreatedFrom = request.CreatedFrom;
    }
    if (request.CreatedTo !== undefined) {
      body.CreatedTo = request.CreatedTo;
    }
    if (request.FormID !== undefined) {
      body.FormID = request.FormID;
    }

    return this.postQuery<ListFormDataResponse>(EP.LIST, body);
  }

  async listFormRecords(formID: number, request: Record<string, unknown> = {}): Promise<ApiResponse<{ items: FormRecord[]; totalCount: number; totalPages: number }>> {
    return this.postQuery<{ items: FormRecord[]; totalCount: number; totalPages: number }>(EP.BY_FORM, { FormID: formID, ...request });
  }

  async getFormRecord(recordId: number): Promise<ApiResponse<FormRecord>> {
    return this.postQuery<FormRecord>(EP.GET, { ID: { ID: recordId } });
  }

  async deleteFormRecord(id: number): Promise<ApiResponse<boolean>> {
    return this.delete<boolean>(EP.DELETE(id));
  }
}

export const formDataApiClient = new FormDataApiClient();
